<template>
	<div>
		<headful
			:title="'Caseen Case Editor -- ' + caseData.title"
		/>
		<h1 style="margin: 24px;">
			Case Editor
		</h1>
		<md-speed-dial class="md-top-right" md-direction="bottom" md-event="click">
			<div>
				<md-speed-dial-target class="md-primary md-mini">
					<md-icon class="md-morph-initial">settings</md-icon>
					<md-icon class="md-morph-final">close</md-icon>
				</md-speed-dial-target>
				<md-tooltip md-direction="left">
					Case Admin
				</md-tooltip>
			</div>
			<md-speed-dial-content>
				<div>
					<md-button class="md-icon-button md-raised" 
					target="_blank" :href="'/editor/' + caseID + '/dashboard'">
						<md-icon>bar_chart</md-icon>
					</md-button>
					<md-tooltip md-direction="left">
						Response Data
					</md-tooltip>
				</div>
				<div>
					<md-button class="md-icon-button" @click="showCaseReviewDialog=true">
						<md-icon>rate_review</md-icon>
					</md-button>
					<md-tooltip md-direction="left">
						Case Reviews
					</md-tooltip>
				</div>
				<div>
					<md-button class="md-icon-button" @click="showCaseSettingsDialog=true">
						<md-icon>dvr</md-icon>
					</md-button>
					<md-tooltip md-direction="left">
						Case Settings
					</md-tooltip>
				</div>
				<div>
					<md-button class="md-icon-button" @click="duplicateCase()">
						<md-icon>file_copy</md-icon>
					</md-button>
					<md-tooltip md-direction="left">
						Duplicate Case
					</md-tooltip>
				</div>
			</md-speed-dial-content>
		</md-speed-dial>
		<hr>
		<div class="case-editor">
			<div id="case-header">
				<md-field id="case-title-input">
					<md-input 
						v-model="caseData.title"
						@change="saveSlide([null, false])"
						maxlength="60"
						placeholder="Case Title"
					/>
				</md-field>
				<md-field id="case-desc-input">
					<md-textarea 
						v-model="caseData.description"
						@change="saveSlide([null, false])"
						placeholder="Case Blurb"
						md-autogrow
						maxlength="300"
					></md-textarea>
				</md-field>
				<div style="margin: auto; width: 100px; text-size: 80%; text-align: center;">
					<md-field id="case-time-input">
						<label>Time Limit</label>
						<md-input 
							v-model.number="caseData.time_limit"
							type="number"
							min=0
							@change="validateTimeLimit"
						/>
						<md-tooltip md-direction="bottom">
							Setting the time limit to 0 will disable the timer for this case.
						</md-tooltip>
						<div class="md-suffix">min</div>
					</md-field>
				</div>
				<div class="md-elevation-3 alert-bar" v-if="caseData.archived">
					This case is currently archived and is not visible to learners.
					<a @click="showCaseSettingsDialog=true; caseSettingsDialogActiveTab='settings-tab-archive'">
						Click here to publish the case.
					</a>
				</div>
			</div>
			<hr>
			<div style="margin: auto; display: inline-block; padding: 20px;">
				<h3 style="margin-top: 0px; margin-bottom: 10px">
						Instructions:
				</h3>
				<span style="font-size: 110%; text-align: justify; float: left;">
						- Add slides below to construct the elements of your case (history, labs, etc). 
						<br>
						- Click/tap and drag slides to change the order in which they are shown to the learner.
						<br>
						- Double click/tap slides in order to begin editing their content.
				</span>
			</div>
			<hr>
			<div>
				<draggable id="slide-sorter" :list="slideDeck" @start="drag=true" @end="drag=false; setActiveSlide()" @update="checkMove"> 
					<md-card 
						v-for="(slide, slideIndex) in slideDeck" 
						:class="{'draggable-slide': true, 'active': activeSlide.slide && activeSlide.slide.order == slide.slide.order}" 
						:key="slide.slide.order" md-with-hover
					> 
						<md-ripple>
							<div class="draggable-slide-content" v-on:dblclick="setActiveSlide(slideIndex)">
								<div>
									#{{slide.slide.order}}
									<br>
									{{slide.type}}
								</div>
									<md-tooltip md-direction="top" v-if="slide.timepoint">
										{{slide.timepoint}}
									</md-tooltip>
							</div>
						</md-ripple>
					</md-card>
					<div class="add-slide" style="border: none">
							<md-button class="md-primary md-raised" @click="showNewSlideDialog=true">
									Add Slide
							</md-button>
					</div>

				</draggable>
			</div>
			<hr>
			<div style="padding: 20px; position: relative;">
				<CaseInfoEditor :slide="activeSlide" v-if="['History', 'Physical Exam', 'Education'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide"/>
				<LabsEditor :slide="activeSlide" :useVerticalLayout="useVerticalLayout" :showInactivityDialog="showInactivityDialog"  :costData="costData" v-if="['Labs'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide" v-on:notify="notify"/>
				<SAQuestionEditor :slide="activeSlide" v-if="['Short Answer'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide"/>
				<MCQuestionEditor :slide="activeSlide" v-if="['Multiple Choice'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide"/>
				<CKQuestionEditor :slide="activeSlide" v-if="['Checklist'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide"/>
				<DiagnosisEditor :slide="activeSlide" v-if="['Diagnosis'].includes(activeSlide.type)" v-on:saveSlide="saveSlide" v-on:deleteSlide="deleteSlide"/>
			</div>
		</div>
		<!-- Case Review Dialog -->
		<md-dialog :md-active.sync="showCaseReviewDialog" :class="{'vertical-layout': useVerticalLayout}" style="min-height: 96%;">
			<md-dialog-title>
				Case Reviews
				<a style="float: right; color: white; font-weight: bold; cursor: pointer; text-decoration: none;" @click="showCaseReviewDialog=false">
					X
				</a>	
			</md-dialog-title>
			<md-dialog-content>
				<div style="padding: 0px 20px 20px 20px">
					<md-card
					v-for="(review, reviewIndex) in caseReviews"
					:key="reviewIndex"
					style="margin: 25px 0px; padding: 10px;">
						<center>
							<i>
								Attempt: #{{review.attempt}}
								<br>
								{{timestampFromID(review.id)}}
							</i>
							<hr>
							<div style="display: flex; justify-content: space-around;">
								<div style="display: inline-block">
										<star-rating 
										v-model="review.quality"
										:show-rating="false"
										:max-rating="5" 
										:increment="0.1"
										:star-size="18" 
										style="display: inline-block">
										</star-rating>
										<br>
										Quality: {{review.quality}}
								</div>
								<div style="display: inline-block">
										<star-rating 
										read-only
										v-model="review.difficulty" 
										:show-rating="false"
										:max-rating="3" 
										:increment="0.1"
										:star-size="18" 
										style="display: inline-block">
										</star-rating>
										<br>
										Difficulty: {{review.difficulty}}
								</div>
							</div>
						</center>
						<hr>
						<p v-if="review.comments" style="padding: 5px 15px 0px 15px">
							<b>Comments:</b> 
							<span v-html="sanitize(review.comments)"></span> 
						</p>
					</md-card>
				</div>
			</md-dialog-content>
		</md-dialog>
		<!-- Case Settings Dialog -->
		<md-dialog :md-active.sync="showCaseSettingsDialog" :class="{'vertical-layout': useVerticalLayout}">
			<md-dialog-title>Case Settings</md-dialog-title>
			<md-dialog-content>
				<md-tabs :md-active-tab="caseSettingsDialogActiveTab">
					<md-tab id="settings-tab-colab" md-label="Editor List" v-if="caseData.creatorID == $store.state.jwtClaims.id">
						<CaseEditorForm
							:caseID="caseData.id"
							v-on:notify="notify"
						/>
					</md-tab>
					<md-tab id="settings-tab-thumbnail" md-label="Case Thumbnail" v-if="caseData.thumbnail">
						<md-field>
								<label>Resource URL:</label>
								<md-input v-model="caseData.thumbnail.res_url"></md-input>
						</md-field>
						<div v-if="!caseData.thumbnail.res_url">
								<vue-dropzone id="imgDropzone" :options="imgDropzoneOptions" v-on:vdropzone-success="thumbnailUploaded">
								</vue-dropzone>
						</div>
						<a :href="caseData.thumbnail.res_url" target="_blank" v-if="caseData.thumbnail.res_url"> 
								Resource test link 
						</a> 
					</md-tab>
					<md-tab id="settings-tab-archive" md-label="Archive Case">
						<md-switch v-model="caseData.archived" class="md-primary">
							Case Archived
						</md-switch>
						<br>
						<i v-if="caseData.archived">
							Archived cases will only be visible to editors of this case, and users who have already completed the case
						</i>
					</md-tab>
				</md-tabs>
			</md-dialog-content>
			<md-dialog-actions>
				<md-button class="md-primary md-raised" @click="saveSlide([null, false]); showCaseSettingsDialog=false;">
					Save
				</md-button>
			</md-dialog-actions>
		</md-dialog>
		<!-- New Slide Dialog -->
		<md-dialog :md-active.sync="showNewSlideDialog" :class="{'vertical-layout': useVerticalLayout}">
			<md-dialog-title>Create Slide</md-dialog-title>
			<md-dialog-content>
				<md-field>
					<label for="movie">New Slide Type</label>
					<md-select v-model="newSlideType">
						<md-option v-for="slideType in slideTypeList" :key="slideType" :value="slideType">
							{{slideType}}
						</md-option>
					</md-select>
				</md-field>
			</md-dialog-content>
			<md-dialog-actions>
				<md-button class="md-primary" @click="showNewSlideDialog=false">Close</md-button>
				<md-button class="md-primary md-raised" @click="createSlide()">Save</md-button>
			</md-dialog-actions>
		</md-dialog>
		<!-- Notification Snackbar -->
		<md-snackbar md-position="left" :md-duration="notificationDuration" :md-active.sync="showNotification">
			<span>{{notificationContents}}</span>
			<md-button class="md-primary" @click="showNotification=false">X</md-button>
		</md-snackbar>
		<!-- Inactivity Dialog --> 
		<md-dialog :md-active.sync="showInactivityDialog" @md-clicked-outside="cancelInactivityClose">
			<md-dialog-title>Editor Timeout</md-dialog-title>
			<md-dialog-content>
				<p style="padding: 24px; text-align: center; font-weight: bold;">
					Editor will timeout in {{inactivityCloseTimeout/(60 * 1000)}} minutes, allowing others to edit this case. 
					<br>
					Click below to resume editing.
				</p>
			</md-dialog-content>
			<md-dialog-actions>
			<md-button class="md-raised md-primary" @click="cancelInactivityClose">Resume Editing</md-button>
		</md-dialog-actions>
		</md-dialog>


	</div>
</template>

<script>
	import StarRating from 'vue-star-rating'
	import CaseInfoEditor from './editors/CaseInfoEditor.vue'
	import LabsEditor from './editors/LabsEditor.vue'
	import SAQuestionEditor from './editors/SAQuestionEditor.vue'
	import MCQuestionEditor from './editors/MCQuestionEditor.vue'
	import CKQuestionEditor from './editors/CKQuestionEditor.vue'
	import DiagnosisEditor from './editors/DiagnosisEditor.vue'
	import CaseEditorForm from './editors/CaseEditorForm.vue'
	import draggable from 'vuedraggable'
	import vueDropzone from 'vue2-dropzone'

	const ObjectID = require("bson-objectid");
	const DOMPurify = require("dompurify")()

	import APIRequests from "./APIRequests.js"

	export default {
		props: {
			caseID: {
				type: String
			},
			useVerticalLayout: {
				type: Boolean
			},
			slideIndex: {
				type: String,
			},
		},
		components: {
			StarRating,
			CaseInfoEditor,
			LabsEditor,
			DiagnosisEditor,
			MCQuestionEditor,
			CKQuestionEditor,
			SAQuestionEditor,
			CaseEditorForm,
			draggable,
			vueDropzone
		},
		data: function () {
			return {
				active: 's1',
				caseData: {},
				costData: {},
				slideDeck: [],
				activeSlide: {},
				showNotification: false,
				notificationDuration: 2500,
				notificationContents: "",
				showCaseSettingsDialog: false,
				caseSettingsDialogActiveTab: "settings-tab-colab",
				showCaseReviewDialog: false,
				showNewSlideDialog: false,
				slideTypeList: ["History", "Physical Exam", "Labs", "Short Answer", "Multiple Choice", "Checklist", "Diagnosis", "Education"],
				newSlideType: "History",
				caseReviews: [],
				serverSocket: undefined,
				inactivityTimeout: 30 * 60 * 1000,
				inactivityTimer: undefined,
				inactivityCloseTimeout: 10 * 60 * 1000, 
				inactivityCloseTimer: undefined,
				showInactivityDialog: false,
			}
		},
		methods: {
			sanitize: function(content){
				return DOMPurify.sanitize(content)
			},
			timestampFromID: function(idString){
				return ObjectID(idString).getTimestamp().toLocaleString();
			},
			fetchCostData: function(){
				APIRequests.getJSONData(
						this.$store.state.api_url + "/partB_costs.json",
						this.$store.state.jwtToken
				).then((response) => {
					this.costData = response
				}).catch(() => {
					this.notify("Could not load test cost data! Please refresh and try again...")
				})
			},
			fetchCaseReviews: function(){
				return new Promise((resolve, reject) => {
					let api_url = this.$store.state.api_url + "/api/cases/" + this.caseID + "/reviews"
					APIRequests.getJSONData(
						api_url, 
						this.$store.state.jwtToken
					).then((data) => {
						if(!data){
							data = []
						}
						this.caseReviews = data;
						this.serverError = undefined;
						resolve()
					}).catch(([xhrStatus, xhrResp]) => {
						this.serverError = xhrStatus
						reject([xhrStatus, xhrResp])
					})
				})
			},
			fetchCase: function (callback) {
				var api_url = this.$store.state.api_url + "/api/cases/" + this.caseID;
				APIRequests.getJSONData(api_url, this.$store.state.jwtToken).then((responseData) => {
					//Check if user is allowed to edit this case
					if(this.$store.state.jwtClaims){
						var isCreator = this.$store.state.jwtClaims.id == responseData.creatorID
						var isEditor = responseData.editorList.includes(this.$store.state.jwtClaims.id)
						if(isCreator || isEditor){
							this.caseData = responseData;
							callback()
							return
						}
					}
					throw [400, "Front-end authentication error"];
				}).catch(() => {
					this.$router.replace("/editor")
				}).then(() => {
					return this.fetchCaseReviews()
				})
			},
			editSlide: function(slide, mode){
				function updateSlide(slideList){
					if(!slideList) slideList = []
					if(mode == "create"){
						slideList.push(slide)
					}
					else if(mode == "delete"){
						for (var i in slideList) {
							if(slideList[i].id == slide.id){
								slideList.splice(i, 1);
							}
						}
					}
					else if(mode=="edit"){
						for (var j in slideList) {
							if(slideList[j].id == slide.id){
								slideList[j] = slide;
							}
						}
					}
					return slideList
				}
				if (slide.type == "History") this.caseData.history = updateSlide(this.caseData.history) 
				if (slide.type == "Physical Exam") this.caseData.physical_exams = updateSlide(this.caseData.physical_exams) 
				if (slide.type == "Labs") this.caseData.labs = updateSlide(this.caseData.labs) 
				if (slide.type == "Short Answer") this.caseData.sa_questions = updateSlide(this.caseData.sa_questions) 
				if (slide.type == "Multiple Choice") this.caseData.mc_questions = updateSlide(this.caseData.mc_questions) 
				if (slide.type == "Checklist") this.caseData.ck_questions = updateSlide(this.caseData.ck_questions) 
				if (slide.type == "Diagnosis") this.caseData.ddx_questions = updateSlide(this.caseData.ddx_questions) 
				if (slide.type == "Education") this.caseData.education = updateSlide(this.caseData.education) 
			},
			createSlideDeck: function(){
				var slideDeck = {}
				var collectSlides = function(activityObj, activityName){
					for (var i in activityObj) {
						var entry = activityObj[i]
						entry.type = activityName
						entry.complete = false
						slideDeck[entry.slide.order] = entry
					}
				}
				collectSlides(this.caseData.history, "History")
				collectSlides(this.caseData.physical_exams, "Physical Exam")
				collectSlides(this.caseData.labs, "Labs")
				collectSlides(this.caseData.sa_questions, "Short Answer")
				collectSlides(this.caseData.mc_questions, "Multiple Choice")
				collectSlides(this.caseData.ck_questions, "Checklist")
				collectSlides(this.caseData.ddx_questions, "Diagnosis")
				collectSlides(this.caseData.education, "Education")
				this.slideDeck = Object.values(slideDeck)
			},
			resetResourceOrder: function(){
				for (var i = 0; i < this.slideDeck.length; i++) {
					this.slideDeck[i].slide.order = i+1
				}
			},
			checkMove: function(){
				this.resetResourceOrder()
				APIRequests.postJSONData(
					this.$store.state.api_url + "/api/editor/updateCase", 
					this.caseData,
					this.$store.state.jwtToken
				).then(() => {
					this.notify("Edits saved successfully!")
				}).catch(([xhrStatus, xhrResp]) => {
					this.notify("Error, failed to save edits: " + xhrStatus + " - " + xhrResp)
				}) 
			}, 
			setActiveSlide: function(slideIndex){
				this.saveSlide([null, false])
				if(slideIndex != undefined){
					this.activeSlide = this.slideDeck[slideIndex]
					this.$router.push("/editor/" + this.caseID + "/" + slideIndex)
				}
				else{
					this.activeSlide = {}
					this.$router.push("/editor/" + this.caseID + "/")
				}
			},
			notify: function(notificationContents, notificationDuration=2500){
				this.notificationContents = notificationContents;
				this.showNotification = true;
				this.notificationDuration = notificationDuration;
			},
			createSlide: function(){
				this.showNewSlideDialog = false;
				var id  = ObjectID();
				var newSlideData = {
					id: id.toString(),
					type: this.newSlideType,
					slide: {
						order: this.slideDeck.length + 1,
						resources: [],
						citations: [],
					}
				}
				if(newSlideData.type == "Multiple Choice"){
					newSlideData.answer_option_id = (new ObjectID).toString()
				} else if(newSlideData.type == "Checklist"){
					newSlideData.answer_option_id = []
				}
				this.editSlide(newSlideData, "create")
				this.createSlideDeck()
				this.saveSlide([null, false])
				if(!this.activeSlide.type){
					this.setActiveSlide(this.slideDeck.length-1)
				}
			},
			saveSlide: function([updatedSlide, closeSlide]){
				if(updatedSlide){
					this.editSlide(updatedSlide, "edit")
				}
				this.restartInactivityTimer()
				return APIRequests.postJSONData(
					this.$store.state.api_url + "/api/editor/updateCase", 
					this.caseData,
					this.$store.state.jwtToken
				).then(() => {
						if(closeSlide){
							this.setActiveSlide()
						}
						this.notify("Edits saved successfully!")
				}).catch(([xhrStatus, xhrResp]) => {
					this.notify("Error, failed to save edits: " + xhrStatus + " - " + xhrResp)
				})
			},
			deleteSlide: function(targetSlide){
				var confirmedDelete = confirm("Are you sure you want to delete slide #" + targetSlide.slide.order + " - " + targetSlide.type + "?")
				if(confirmedDelete){
					this.editSlide(targetSlide, "delete")
					this.createSlideDeck()
					this.resetResourceOrder()
					this.saveSlide([null, true])
				}
			},
			thumbnailUploaded: function(file, response){
					this.caseData.thumbnail.name = file.name
					this.caseData.thumbnail.res_url = this.$store.state.api_url + "/uploads/" + response
					this.saveSlide([null, false])
			},
			closeEditorSession: function(){
				this.saveSlide([null, false])
				if(this.serverSocket != undefined){
					this.serverSocket.onclose = () => {}
					this.serverSocket.close()
				}
			},
			restartInactivityTimer: function(){
				clearTimeout(this.inactivityTimer)
				this.inactivityTimer = setTimeout(this.timeoutInactiveSession, this.inactivityTimeout)	
			},
			timeoutInactiveSession: function(){
				this.inactivityCloseTimer = setTimeout(() => {
					this.saveSlide([null, false])
					this.closeEditorSession()
					alert("Your editing session has timed out, allowing other users to edit the case.")
					window.location.reload()
				}, this.inactivityCloseTimeout)
				this.showInactivityDialog = true
			},
			cancelInactivityClose: function(){
				this.showInactivityDialog = false; 
				clearTimeout(this.inactivityCloseTimer);
				this.restartInactivityTimer();
			},
			openSocketToServer: function(){
				if (!("WebSocket" in window)) {
					alert("Websockets are not available for your browser.\nPlease upgrade to a more modern browser for a better user experience.")
					return
				}
				var ws_base_url = this.$store.state.api_url
				if(ws_base_url == ""){
					ws_base_url = window.location.protocol + "//" + window.location.host
				}
				ws_base_url = ws_base_url.replace("http://", "ws://").replace("https://", "wss://")
				this.serverSocket = new WebSocket(ws_base_url + "/api/ws?jwt=" + this.$store.state.jwtToken);
				
				this.serverSocket.onopen = () => {
					this.serverSocket.send(JSON.stringify({
						type: "registerEditor",
						data: this.caseData.id
					}));
					this.restartInactivityTimer()
				};
					
				this.serverSocket.onmessage = (rawMessage) => { 
					var incomingData = JSON.parse(rawMessage.data);
					if(incomingData.type == "caseLock") {
						alert("\nAnother collaborator (" + incomingData.data + ") is currently editing this case.\n\nEditing is locked to prevent you from overwriting their work! Please get in contact to coordinate further editing.")
						this.$router.push("/editor")
					}
					if(incomingData.type == "colabLockedOut") {
						this.notify("Another collaborator (" + incomingData.data + ") would like to edit this case!", Infinity)
					}
				};
					
				this.serverSocket.onclose = () => { 
					setTimeout(() => {
						this.openSocketToServer();
					}, 1000)
				};
			},
			duplicateCase: function(){
				var copyConfirmed = confirm("Are you sure you want to make a copy of this case?")
				if(copyConfirmed){
					APIRequests.getJSONData(
						this.$store.state.api_url + "/api/editor/" + this.caseID + "/duplicate",
						this.$store.state.jwtToken
					).then((data) => {
						window.location = "/editor/" + data.newID
					}).catch((xhrResp) => {	
						this.notify("Failed to duplicate case: " + xhrResp[1])
					})
				}
				return
			},
			validateTimeLimit: function() {
				// Validates the case time-limit
				if (typeof this.caseData.time_limit != 'number' || this.caseData.time_limit < 0) {
					this.caseData.time_limit = 0;
				}
				this.saveSlide([null, false]);
			}
		},
		computed: {
			imgDropzoneOptions: function() {
				return {
					maxFiles: 1,
					url: this.$store.state.api_url + "/api/editor/uploadFile?jwt=" + this.$store.state.jwtToken,
					thumbnailWidth: 150,
					acceptedFiles: "image/*",
					maxFilesize: 25, //megabytes
				}
			},
		},
		mounted: function () {
			this.$nextTick(function () {
				this.fetchCase(() => {
					this.fetchCostData()
					this.createSlideDeck()
					let activeIndex = parseInt(this.slideIndex)
					if(!isNaN(activeIndex)) this.activeSlide = this.slideDeck[activeIndex]
					this.openSocketToServer()
					window.addEventListener("blur", () => {
						this.saveSlide([null, false])
					})
					window.onbeforeunload = () => this.saveSlide([null, false])
				})
			})
		},
		beforeDestroy: function () {
			window.onbeforeunload = () => {}
		},
		destroyed: function() {
			this.closeEditorSession()
		}
	}
</script>

<style scoped>
	.md-dialog{
		overflow: auto;
	}
	.md-dialog:not(.vertical-layout) {
		min-width: 500px;
		max-width: 650px;
	}
</style>
<style>
	h2{
		font-size: 170%;
	}
	#case-header{
		width: 80%;
		margin: auto;
	}
	#case-title-input input{
		font-size: 150%;
		text-align: center;
	}
	#case-desc-input textarea{
		font-size: 95%; 
		text-align: center; 
		font-style: italic;
	}
	#case-time-input label {
		padding-left: 20px;
	}
	#case-time-input input {
		text-align: center; 
		width: 100px;
	}
	#slide-sorter {
		display: flex;
		overflow-x: scroll;
		padding: 20px 0;
	}

	.draggable-slide .md-ripple {
		width: 120px !important;
		height: 120px !important;
	}

	.draggable-slide-content {
		display: flex;
		align-content: space-around;
		justify-content: space-around;
		flex-direction: column;
		width: 120px;
		height: 120px;
		cursor: grab;
	}

	.draggable-slide {
		border-radius: 3px;
	}

	.draggable-slide.active {
		border: 1px solid #448aff
	}
	.add-slide{
		position: relative;
		flex-direction:column; 
		width: 120px;
		height: 120px;
		display: flex;
		justify-content:center;
		align-content:center;
	}
	.md-dialog{
		border-radius: 5px;
	}
	.md-dialog-container{
		width: 100%
	}
	.md-dialog-content{
		padding: 16px 40px; 
	}
	.md-dialog-actions{
		position: relative;
		padding: 10px 20px; 
		background-color: #ddd;
		margin-top: 3px;
		box-shadow: 0 -3px 1px -2px rgba(0,0,0,.15), 0 -2px 2px 0 rgba(0,0,0,.1), 0 -1px 5px 0 rgba(0,0,0,.08);
	}
	.md-dialog-title{
		text-align: center;
		font-size: 16px;
		background-color: #448aff;
		color: white;
		margin-bottom: 3px;
		padding: 6px 24px;
		box-shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
	}
	.slide-delete-btn{ 
		position: absolute;
		top: 15px;
		right: 15px;
	}
	.alert-bar{
		border: 2px ridge #bbb; 
		font-size: 85%; 
		margin: 10px auto 20px auto;
		display: inline-block; 
		padding: 10px 25px; 
		background-color: #ffe17f; 
		border-radius: 2px;
		font-weight: bold;
		color: #4c3f26; 
	}
	.alert-bar a { 
		color: #19150c !important; 
		cursor: pointer;
	}
</style>